import { Skeleton, Tooltip, Typography } from '@mui/material';
import React from 'react';
import { generateUuidV4 } from '../../../util/uuid';
import { log } from '../../../util/log';
import { MetadataError } from '@ep/error-handling';
import { FeedbackSnackbarContext } from '../../../context/FeedbackSnackbarContext';

interface SDGIconProps {
  sdg: number;
  width?: number;
}

const sdg_labels: Array<string> = [
  'No Poverty',
  'Zero Hunger',
  'Good Health and Well-Being',
  'Quality Education',
  'Gender Equality',
  'Clean Water and Sanitation',
  'Affordable and Clean Energy',
  'Decent Work and Economic Growth',
  'Industry, Innovation and Infrastructure',
  'Reduced Inequalities',
  'Sustainable Cities and Communities',
  'Responsible Consumption and Production',
  'Climate Action',
  'Life Below Water',
  'Life On Land',
  'Peace, Justice and Strong Institutions',
  'Partnerships for the Goals'
];

/**
 * Displays a given SDG icon by goal number.
 * @param {number} sdg SDG goal number (1 - 17) to be displayed.
 * @param {number | undefined} width Optionally control the width of the icon.
 * @returns {JSX.Element}
 */
const SDGIcon = ({ sdg, width }: SDGIconProps) => {
  const { setFeedbackData } = React.useContext(FeedbackSnackbarContext);
  const [imageSrc, setImageSrc] = React.useState<string | null>(null);

  React.useEffect(() => {
    import(`../../../assets/sdgs/sdg_${sdg}.svg`)
      .then((imageModule) => {
        setImageSrc(imageModule.default);
      })
      .catch((err: unknown) => {
        const tracking_id: string = generateUuidV4();
        log(
          'error',
          new MetadataError(
            err instanceof Error
              ? err.message
              : 'Error: SDGIcon failed on an unknown error while loading image.',
            {
              sdg: sdg
            },
            tracking_id
          )
        );
        setFeedbackData({
          message: `Unable to load SDG icon. Tracking ID: ${tracking_id}`,
          state: true,
          type: 'error'
        });
      });
  }, [sdg]);

  return (
    <Tooltip
      arrow
      title={
        <Typography sx={{ fontSize: '0.9rem' }}>
          {sdg_labels[sdg - 1] ?? 'Sustainable Development Goal'}
        </Typography>
      }
    >
      {imageSrc ? (
        <img
          src={imageSrc}
          alt={sdg_labels[sdg - 1]}
          style={width ? { width: `${width}px`, borderRadius: '6px' } : undefined}
        />
      ) : (
        <Skeleton sx={{ backgroundColor: '#7D8F99', height: width ?? 50, width: width ?? 50 }} />
      )}
    </Tooltip>
  );
};

export default SDGIcon;
